import React, { useEffect, useRef, useState } from "react";
import { storeApi } from "@/lib/storeApi";
import { getConsent } from "@/lib/metaPixel";
import { ChatCircleDotsIcon, XIcon, PaperPlaneRightIcon } from "@phosphor-icons/react";

const SEED = [
  { role: "assistant", text: "Hi! I'm the BotGenomix help assistant. Ask me about plans, licences, installing the software or how our testing works." },
];

export default function HelpWidget() {
  const [open, setOpen] = useState(false);
  const [consented, setConsented] = useState(!!getConsent());
  useEffect(() => {
    const on = () => setConsented(true);
    window.addEventListener("bgx-consent", on);
    return () => window.removeEventListener("bgx-consent", on);
  }, []);
  const [msgs, setMsgs] = useState(SEED);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sid] = useState(() => `store-${Math.random().toString(36).slice(2, 10)}`);
  const boxRef = useRef(null);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const r = await storeApi.post("/store/help", { message: text, session_id: sid }, { timeout: 45000 });
      setMsgs((m) => [...m, { role: "assistant", text: r.data?.reply || "…" }]);
    } catch (err) {
      const detail = err?.response?.data?.reply || err?.message || "network error";
      setMsgs((m) => [...m, { role: "assistant", text: `Sorry, help is temporarily unavailable — ${detail}. Please try again in a moment.` }]);
      console.error("help widget error", err);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {!open && consented && (
        <button
          data-testid="help-widget-open"
          onClick={() => setOpen(true)}
          aria-label="Ask helpline"
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 flex items-center gap-2 p-3.5 sm:px-4 sm:py-3 rounded-full bg-[#D6FF38] text-black mono uppercase tracking-widest text-[10px] font-medium shadow-lg hover:scale-105 transition-transform"
        >
          <ChatCircleDotsIcon size={18} weight="fill" />
          <span className="hidden sm:inline">Ask helpline</span>
        </button>
      )}

      {open && (
        <div
          data-testid="help-widget-panel"
          className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:bottom-6 sm:right-6 z-40 sm:w-[92vw] max-w-sm h-[70vh] max-h-[520px] border border-[#2A2D3A] bg-[#0F1116] rounded-lg shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#2A2D3A] bg-[#15171E]">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-[#22D3EE]">Live help</div>
              <div className="text-sm font-medium">BotGenomix Assistant</div>
            </div>
            <button
              data-testid="help-widget-close"
              onClick={() => setOpen(false)}
              className="text-[#64748B] hover:text-[#F1F5F9]"
              aria-label="Close chat"
            >
              <XIcon size={16} />
            </button>
          </div>

          <div ref={boxRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3">
            {msgs.map((m, i) => (
              <div
                key={i}
                data-testid={`help-msg-${m.role}`}
                className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-[#D6FF38] text-black"
                    : "mr-auto bg-[#15171E] border border-[#2A2D3A] text-[#F1F5F9]"
                }`}
              >
                {m.text}
              </div>
            ))}
            {busy && (
              <div className="mr-auto max-w-[85%] rounded-lg px-3 py-2 text-sm bg-[#15171E] border border-[#2A2D3A] text-[#64748B] mono">
                thinking…
              </div>
            )}
          </div>

          <div className="border-t border-[#2A2D3A] p-3 flex items-center gap-2">
            <input
              data-testid="help-widget-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ask how to do something on the site…"
              className="flex-1 bg-[#15171E] border border-[#2A2D3A] rounded-md px-3 py-2 text-sm text-[#F1F5F9] placeholder:text-[#475569] focus:outline-none focus:border-[#22D3EE]"
              maxLength={500}
            />
            <button
              data-testid="help-widget-send"
              onClick={send}
              disabled={busy || !input.trim()}
              className="p-2 rounded-md bg-[#D6FF38] text-black disabled:opacity-40 hover:scale-105 transition-transform"
              aria-label="Send"
            >
              <PaperPlaneRightIcon size={16} weight="fill" />
            </button>
          </div>
          <div className="px-3 pb-2 text-[11px] text-[#94A3B8] leading-snug">
            Website guide only - it can't give financial, legal or tax advice or help you choose. Past performance is not a reliable indicator of future results.
          </div>
        </div>
      )}
    </>
  );
}
